import type { HsHypersceneTypes } from '../types/hyperscene-types.type';

export class HsQuaternion {
  public readonly values: HsHypersceneTypes.Array4;

  constructor(x: number, y: number, z: number, w: number) {
    this.values = [x, y, z, w];
  }

  public get x(): number {
    return this.values[0];
  }

  public get y(): number {
    return this.values[1];
  }

  public get z(): number {
    return this.values[2];
  }

  public get w(): number {
    return this.values[3];
  }

  /**
   * Sets set.
   * @param x x.
   * @param y y.
   * @param z z.
   * @param w w.
   * @returns Result value.
   */
  public set(x: number, y: number, z: number, w: number): HsQuaternion {
    this.values[0] = x;
    this.values[1] = y;
    this.values[2] = z;
    this.values[3] = w;
    return this;
  }

  /**
   * Sets from array.
   * @param array array.
   * @returns Result value.
   */
  public setFromArray(array: HsHypersceneTypes.Array4): HsQuaternion {
    this.values[0] = array[0];
    this.values[1] = array[1];
    this.values[2] = array[2];
    this.values[3] = array[3];
    return this;
  }

  /**
   * Copies copy.
   * @param other other.
   * @returns Result value.
   */
  public copy(other: HsQuaternion): HsQuaternion {
    return this.set(other.x, other.y, other.z, other.w);
  }

  /**
   * Sets from rotation matrix.
   * @param matrixElements matrix elements.
   * @returns Result value.
   */
  public setFromRotationMatrix(matrixElements: HsHypersceneTypes.Array16): HsQuaternion {
    const element11: number = matrixElements[0];
    const element12: number = matrixElements[4];
    const element13: number = matrixElements[8];
    const element21: number = matrixElements[1];
    const element22: number = matrixElements[5];
    const element23: number = matrixElements[9];
    const element31: number = matrixElements[2];
    const element32: number = matrixElements[6];
    const element33: number = matrixElements[10];
    const trace: number = element11 + element22 + element33;
    let scaleMatrix: number = 1;
    if (trace > 0) {
      scaleMatrix = 0.5 / Math.sqrt(trace + 1);
      this.values[3] = 0.25 / scaleMatrix;
      this.values[0] = (element32 - element23) * scaleMatrix;
      this.values[1] = (element13 - element31) * scaleMatrix;
      this.values[2] = (element21 - element12) * scaleMatrix;
      return this;
    }
    if (element11 > element22 && element11 > element33) {
      scaleMatrix = 2.0 * Math.sqrt(1 + element11 - element22 - element33);
      this.values[3] = (element32 - element23) / scaleMatrix;
      this.values[0] = 0.25 * scaleMatrix;
      this.values[1] = (element12 + element21) / scaleMatrix;
      this.values[2] = (element13 + element31) / scaleMatrix;
      return this;
    }
    if (element22 > element33) {
      scaleMatrix = 2.0 * Math.sqrt(1 + element22 - element11 - element33);
      this.values[3] = (element13 - element31) / scaleMatrix;
      this.values[0] = (element12 + element21) / scaleMatrix;
      this.values[1] = 0.25 * scaleMatrix;
      this.values[2] = (element23 + element32) / scaleMatrix;
      return this;
    }
    scaleMatrix = 2.0 * Math.sqrt(1 + element33 - element11 - element22);
    this.values[3] = (element21 - element12) / scaleMatrix;
    this.values[0] = (element13 + element31) / scaleMatrix;
    this.values[1] = (element23 + element32) / scaleMatrix;
    this.values[2] = 0.25 * scaleMatrix;
    return this;
  }

  /**
   * Gets length.
   * @returns Result value.
   */
  public getLength(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w);
  }

  /**
   * Handles normalize.
   * @returns Result value.
   */
  public normalize(): HsQuaternion {
    const length: number = this.getLength();

    if (Object.is(length, 0)) {
      this.set(0, 0, 0, 1);
      return this;
    }

    const invertedLength: number = 1 / length;
    this.set(this.x * invertedLength, this.y * invertedLength, this.z * invertedLength, this.w * invertedLength);

    return this;
  }

  /**
   * Sets from axis angle.
   * @param axis axis.
   * @param angleRadians angle radians.
   * @returns Result value.
   */
  public setFromAxisAngle(axis: HsHypersceneTypes.Array3, angleRadians: number): HsQuaternion {
    const halfAngle: number = angleRadians / 2,
      halfAngleSin: number = Math.sin(halfAngle);
    this.set(axis[0] * halfAngleSin, axis[1] * halfAngleSin, axis[2] * halfAngleSin, Math.cos(halfAngle));
    return this;
  }

  /**
   * Handles multiply.
   * @param hsOtherQuaternion s other quaternion.
   * @returns Result value.
   */
  public multiply(otherQuaternion: HsQuaternion): HsQuaternion {
    const currentX: number = this.x,
      currentY: number = this.y,
      currentZ: number = this.z,
      currentW: number = this.w;
    const otherX: number = otherQuaternion.x,
      otherY: number = otherQuaternion.y,
      otherZ: number = otherQuaternion.z,
      otherW: number = otherQuaternion.w;

    this.values[0] = currentX * otherW + currentW * otherX + currentY * otherZ - currentZ * otherY;
    this.values[1] = currentY * otherW + currentW * otherY + currentZ * otherX - currentX * otherZ;
    this.values[2] = currentZ * otherW + currentW * otherZ + currentX * otherY - currentY * otherX;
    this.values[3] = currentW * otherW - currentX * otherX - currentY * otherY - currentZ * otherZ;

    return this;
  }

  /**
   * Handles slerp.
   * @param quaternionA quaternion a.
   * @param quaternionB quaternion b.
   * @param param param.
   * @returns Result value.
   */
  public slerp(quaternionA: HsQuaternion, quaternionB: HsQuaternion, param: number): HsQuaternion {
    this.copy(quaternionA);

    if (param === 0) {
      return this;
    }
    if (param === 1) {
      return this.copy(quaternionB);
    }

    const x: number = this.x;
    const y: number = this.y;
    const z: number = this.z;
    const w: number = this.w;

    let cosHalfTheta: number = w * quaternionB.w + x * quaternionB.x + y * quaternionB.y + z * quaternionB.z;

    if (cosHalfTheta < 0) {
      this.values[3] = -quaternionB.w;
      this.values[0] = -quaternionB.x;
      this.values[1] = -quaternionB.y;
      this.values[2] = -quaternionB.z;

      cosHalfTheta = -cosHalfTheta;
    } else {
      this.copy(quaternionB);
    }

    if (cosHalfTheta >= 1.0) {
      this.values[3] = w;
      this.values[0] = x;
      this.values[1] = y;
      this.values[2] = z;

      return this;
    }

    const sqrSinHalfTheta: number = 1.0 - cosHalfTheta * cosHalfTheta;

    if (sqrSinHalfTheta <= Number.EPSILON) {
      const s: number = 1 - param;
      this.values[3] = s * w + param * this.values[3];
      this.values[0] = s * x + param * this.values[0];
      this.values[1] = s * y + param * this.values[1];
      this.values[2] = s * z + param * this.values[2];

      this.normalize();

      return this;
    }

    const sinHalfTheta: number = Math.sqrt(sqrSinHalfTheta);
    const halfTheta: number = Math.atan2(sinHalfTheta, cosHalfTheta);
    const ratioA: number = Math.sin((1 - param) * halfTheta) / sinHalfTheta;
    const ratioB: number = Math.sin(param * halfTheta) / sinHalfTheta;

    this.values[3] = w * ratioA + this.values[3] * ratioB;
    this.values[0] = x * ratioA + this.values[0] * ratioB;
    this.values[1] = y * ratioA + this.values[1] * ratioB;
    this.values[2] = z * ratioA + this.values[2] * ratioB;

    return this;
  }

  /**
   * Builds identity quaternion.
   * @returns Result value.
   */
  public static makeIdentityQuaternion(): HsQuaternion {
    return new HsQuaternion(0, 0, 0, 1);
  }

  /**
   * Builds from axis and angle.
   * @param axis axis.
   * @param angleRadians angle radians.
   * @returns Result value.
   */
  public static makeFromAxisAndAngle(axis: HsHypersceneTypes.Array3, angleRadians: number): HsQuaternion {
    const halfAngle: number = angleRadians / 2;
    const halfAngleSin: number = Math.sin(halfAngle);
    return new HsQuaternion(
      axis[0] * halfAngleSin,
      axis[1] * halfAngleSin,
      axis[2] * halfAngleSin,
      Math.cos(halfAngle)
    );
  }

  /**
   * Builds copy.
   * @param source source.
   * @returns Result value.
   */
  public static makeCopy(source: HsQuaternion): HsQuaternion {
    return new HsQuaternion(source.x, source.y, source.z, source.w);
  }
}
